import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, switchMap, catchError } from 'rxjs/operators';
import { Observable, EMPTY } from 'rxjs';
import { Action } from '@ngrx/store';

import {
  loadSearchDataAction,
  loadSearchDataSuccessAction,
  saveSearchDataAction,
  saveExchangeSearchDataAction,
  resetAlertsDataAction
} from '@shared/base-search-bar/store/actions/storage.actions';
import { StorageServices } from '@shared/base-search-bar/services/storage.services';
import { loadAlertAction, resetWidgetDataAction, setActiveAlertsUIAction } from '@shared/widgets/store/actions/alerts.action';
import { homeItemDetailRootRoute } from '@home-item-detail/home-item-detail-routing.module';
import { HOTEL_RESULTS_ROUTE } from '@app/modules/hotels/hotel-results/constants/routes';
import { hotelDetailRootRoute } from '@app/modules/hotels/hotel-detail/hotel-detail-routing.module';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { isAnInternationalTripBySearch } from '@app/mappers/is-international-trip.mapper';
import { setInternationalSearchAction } from '@app/store/actions/global.actions';
import { SessionStorageService } from '@app/services/session-storage.service';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
@Injectable()
export class StorageEffects {

  constructor(
    private router: Router,
    private action$: Actions,
    private readonly service: StorageServices,
    private readonly sessionStorage: SessionStorageService) { }

  saveSearchData$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(saveSearchDataAction),
      switchMap(action => {
        const searchDataPassengers = {
          guestsPerRoom: action.data.guestsPerRoom,
          guestsPerRoomState: action.data.guestsPerRoomState,
          passengerArrangement: action.data.passengerArrangement,
          passengerArrangementsState: action.data.passengerArrangementsState,
          flightClass: action.data.flightClass
        }
        this.sessionStorage.setSearchDataPassengers(searchDataPassengers);
        return this.service.save(this.searchDataWithOutPassengers(action.data)).pipe(
          map(() => {
            const isAnInternationalTrip = isAnInternationalTripBySearch(action.searchBy, action.data);
            return setInternationalSearchAction({ payload: isAnInternationalTrip });
          }),
          catchError(() => EMPTY)
        )
      })
    ));

  saveExchangeSearch$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(saveExchangeSearchDataAction),
      switchMap(action => this.service.save(this.searchDataWithOutPassengers(action.data)).pipe(
        switchMap(() => {
          const isAnInternationalTrip = isAnInternationalTripBySearch(SearchBy.FLIGHTS, action.data);
          return [
            loadSearchDataSuccessAction({ data: action.data, searchBy: SearchBy.FLIGHTS, exchange: true }),
            setInternationalSearchAction({ payload: isAnInternationalTrip })
          ];
        }),
        catchError(() => {
          return EMPTY;
        })
      ))
    ));

  resetAlertsDat$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(resetAlertsDataAction),
      switchMap(() => this.service.load().pipe(
        switchMap(() => [resetWidgetDataAction(), setActiveAlertsUIAction({ payload: false })]),
        catchError(() => EMPTY)
      ))
    ));

  loadSearchData$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(loadSearchDataAction),
      switchMap(action => this.service.load().pipe(
        switchMap(_data => {
          const dataPassengers = this.sessionStorage.getSearchDataPassengers();
          const data = { ..._data, ...dataPassengers };
          const isAnInternationalTrip = isAnInternationalTripBySearch(action.searchBy, data);
          const resetWidgetData = this.router.url.includes(homeItemDetailRootRoute);
          const isHotelRoute = this.router.url.includes(hotelDetailRootRoute) || this.router.url.includes(HOTEL_RESULTS_ROUTE);
          const actions = resetWidgetData ? [resetWidgetDataAction()] : isHotelRoute ? [] : [loadAlertAction()];
          return [
            loadSearchDataSuccessAction({ data, searchBy: action.searchBy }),
            setInternationalSearchAction({ payload: isAnInternationalTrip }),
            ...actions
          ];
        }),
        catchError(() => {
          return EMPTY;
        })
      ))
    ));

  public searchDataWithOutPassengers(searchData: SearchData): SearchData {
    return {
      ...searchData,
      guestsPerRoom: [], guestsPerRoomState: { entities: {}, ids: [] },
      passengerArrangement: [], passengerArrangementsState: { entities: {}, ids: [] },
      flightClass: null
    };
  }
}
